<ng-container *ngIf="{
  loadState: loadState$ | async,
  saveState: saveState$ | async,
  filteredFirmware: filteredFirmware$ | async,
  selectedFirmware: selectedFirmware$ | async,
  filterType: filterType$ | async,
  filterLoggerType: filterLoggerType$ | async,
  filterModelFamily: filterModelFamily$ | async,
  searchQuery: searchQuery$ | async
} as state">
  <div class="firmware-metadata-editor tw-flex tw-flex-col tw-h-full tw-gap-4">
    <!-- Header -->
    <lib-block-title [title]="labels.title"></lib-block-title>

    <!-- Error message -->
    <div
      *ngIf="state.loadState?.status === 'error'"
      class="tw-flex tw-items-center tw-gap-2 tw-text-red-600">
      <mat-icon
        *ngIf="uiFramework === 'material'"
        class="s-5">error</mat-icon>
      <ion-icon
        *ngIf="uiFramework === 'ionic'"
        name="alert-circle"></ion-icon>
      <span>{{ state.loadState?.error }}</span>
    </div>

    <!-- Content Area -->
    <div class="tw-flex tw-flex-col tw-gap-4 tw-flex-1 tw-min-h-0">
      <!-- Firmware List Section -->
      <div
        class="tw-flex tw-flex-col tw-gap-3 tw-overflow-hidden"
        [class.tw-flex-1]="!state.selectedFirmware">
        <!-- Selected Firmware Card (shown when firmware is selected) -->
        <ng-container *ngIf="state.selectedFirmware as selected">
        <div class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-accent-100 radius-4 border-1 tw-border-accent-200">
          <mat-icon
            *ngIf="uiFramework === 'material'"
            class="s-5 min-s-5 text-accent">{{ getTypeIcon(selected.firmwareType) }}</mat-icon>
          <ion-icon
            *ngIf="uiFramework === 'ionic'"
            class="s-5 min-s-5 text-accent"
            [name]="getTypeIcon(selected.firmwareType)"></ion-icon>
          <div class="tw-flex tw-flex-col tw-flex-1 tw-min-w-0 w-full">
            <span class="tw-font-medium tw-truncate">{{ selected.metadata?.reference ? selected.metadata?.reference + ' (' + selected.name + ')' : selected.name }}</span>
            <span class="tw-text-sm text-grey-400 tw-font-mono">{{ selected.version }} - {{ getProductLabel(selected.productId) }}</span>
          </div>
          <button
            *ngIf="uiFramework === 'material'"
            class="tw-flex-shrink-0"
            mat-icon-button
            (click)="cancelEdit(); $event.stopPropagation()">
            <mat-icon>close</mat-icon>
          </button>
          <ion-button
            *ngIf="uiFramework === 'ionic'"
            fill="clear"
            (click)="cancelEdit(); $event.stopPropagation()">
            <ion-icon name="close"></ion-icon>
          </ion-button>
        </div>
      </ng-container>

        <!-- Filters Section (hidden when firmware is selected) -->
        <ng-container *ngIf="!state.selectedFirmware">
          <!-- Search (always visible) -->
          <div class="tw-min-w-[200px]">
            <mat-form-field
              *ngIf="uiFramework === 'material'"
              class="custom-mat-form-field w-full custom-border-bottom">
              <input
                matInput
                [placeholder]="labels.searchPlaceholder"
                [value]="state.searchQuery"
                (input)="searchQuery$.next($any($event.target).value)">
              <mat-icon
                class="s-6 text-grey-400"
                matSuffix>search</mat-icon>
            </mat-form-field>
            <ion-searchbar
              *ngIf="uiFramework === 'ionic'"
              [placeholder]="labels.searchPlaceholder"
              [value]="state.searchQuery"
              (ionInput)="searchQuery$.next($any($event)?.detail?.value || '')">
            </ion-searchbar>
          </div>

          <!-- Desktop Filters (hidden on mobile) -->
          <div
            class="tw-flex tw-flex-wrap tw-gap-4 tw-items-end fx-justify-between"
            id="firmware-filters">
            <!-- Type Filter -->
            <div class="tw-flex tw-flex-col tw-gap-2">
              <label class="tw-text-xs tw-font-medium text-grey-400 tw-uppercase">{{ labels.filterByType }}</label>
              <div class="tw-flex tw-gap-2">
                <button
                  class="custom-mat-button-border"
                  mat-button
                  [class.custom-mat-button-primary]="state.filterType === 'all'"
                  [class.custom-mat-button-secondary]="state.filterType !== 'all'"
                  (click)="setFilterType('all')">
                  {{ labels.all }}
                </button>
                <button
                  class="custom-mat-button-border"
                  mat-button
                  [class.custom-mat-button-primary]="state.filterType === 'logger'"
                  [class.custom-mat-button-secondary]="state.filterType !== 'logger'"
                  (click)="setFilterType('logger')">
                  <mat-icon
                    *ngIf="uiFramework === 'material'"
                    class="s-5 m-0">router</mat-icon>
                  <ion-icon
                    *ngIf="uiFramework === 'ionic'"
                    name="git-network-outline"></ion-icon>
                  {{ labels.logger }}
                </button>
                <button
                  class="custom-mat-button-border"
                  mat-button
                  [class.custom-mat-button-primary]="state.filterType === 'inverter'"
                  [class.custom-mat-button-secondary]="state.filterType !== 'inverter'"
                  (click)="setFilterType('inverter')">
                  <mat-icon
                    *ngIf="uiFramework === 'material'"
                    class="s-5 m-0">solar_power</mat-icon>
                  <ion-icon
                    *ngIf="uiFramework === 'ionic'"
                    name="sunny-outline"></ion-icon>
                  {{ labels.inverter }}
                </button>
              </div>
            </div>

          <!-- Tags Filter -->
          <div class="tw-flex tw-flex-col tw-gap-2">
            <label class="tw-text-xs tw-font-medium text-grey-400 tw-uppercase">{{ labels.filterByTags }}</label>
            <div class="tw-flex tw-flex-wrap tw-gap-2">
              <mat-chip
                *ngFor="let tag of suggestedTags"
                [ngClass]="isFilterTagActive(tag) ? 'chip-level-0' : 'chip-inactive'"
                (click)="toggleFilterTag(tag)">
                {{ tag }}
              </mat-chip>
            </div>
          </div>

          <!-- Logger Type Filter (shown when type = logger) -->
          <div
            *ngIf="state.filterType === 'logger'"
            class="tw-flex tw-flex-col tw-gap-2">
            <label class="tw-text-xs tw-font-medium text-grey-400 tw-uppercase">{{ labels.filterByLoggerType }}</label>
            <div class="tw-flex tw-flex-wrap tw-gap-2">
              <button
                class="custom-mat-button-border"
                mat-button
                [class.custom-mat-button-primary]="!state.filterLoggerType"
                [class.custom-mat-button-secondary]="state.filterLoggerType"
                (click)="filterLoggerType$.next('')">
                {{ labels.allLoggerTypes }}
              </button>
              <button
                *ngFor="let lt of loggerTypes"
                class="custom-mat-button-border"
                mat-button
                [class.custom-mat-button-primary]="state.filterLoggerType === lt.label"
                [class.custom-mat-button-secondary]="state.filterLoggerType !== lt.label"
                (click)="filterLoggerType$.next(lt.label)">
                {{ lt.label }}
              </button>
            </div>
          </div>

          <!-- Model Family Filter (shown when type = inverter) -->
          <div
            *ngIf="state.filterType === 'inverter'"
            class="tw-flex tw-flex-col tw-gap-2">
            <label class="tw-text-xs tw-font-medium text-grey-400 tw-uppercase">{{ labels.filterByModelFamily }}</label>
            <div class="tw-flex tw-flex-wrap tw-gap-2">
              <button
                class="custom-mat-button-border"
                mat-button
                [class.custom-mat-button-primary]="!state.filterModelFamily"
                [class.custom-mat-button-secondary]="state.filterModelFamily"
                (click)="filterModelFamily$.next('')">
                {{ labels.allModelFamilies }}
              </button>
              <button
                *ngFor="let mf of inverterModelFamilies"
                class="custom-mat-button-border"
                mat-button
                [class.custom-mat-button-primary]="state.filterModelFamily === mf"
                [class.custom-mat-button-secondary]="state.filterModelFamily !== mf"
                (click)="filterModelFamily$.next(mf)">
                {{ mf }}
              </button>
            </div>
          </div>
        </div>

          <!-- Loading State -->
          <div
            *ngIf="state.loadState?.status === 'loading'"
            class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-32 text-grey-400">
            <mat-spinner
              *ngIf="uiFramework === 'material'"
              diameter="40"></mat-spinner>
            <ion-spinner
              *ngIf="uiFramework === 'ionic'"
              name="crescent"></ion-spinner>
          </div>

          <!-- Empty State -->
          <div
            *ngIf="state.loadState?.status !== 'loading' && state.filteredFirmware?.length === 0"
            class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-32 text-grey-400">
            <mat-icon
              *ngIf="uiFramework === 'material'"
              class="tw-text-4xl tw-mb-2">search_off</mat-icon>
            <ion-icon
              *ngIf="uiFramework === 'ionic'"
              class="tw-text-4xl tw-mb-2"
              name="cube-outline"></ion-icon>
            <span>{{ labels.noFirmware }}</span>
          </div>

          <!-- Firmware Table (Desktop) -->
          <div
            class="tw-flex-1 tw-overflow-auto border-1 radius-4 tw-border-grey-100"
            id="firmware-table">
            <table
              *ngIf="state.loadState?.status !== 'loading' && state.filteredFirmware?.length"
              class="customer-table tw-w-full"
              mat-table
              [dataSource]="state.filteredFirmware || []">
            <!-- Name Column (prioritized) -->
            <ng-container matColumnDef="name">
              <th *matHeaderCellDef mat-header-cell>{{ labels.name }}</th>
              <td
                *matCellDef="let fw"
                class="!tw-font-base"
                mat-cell>
                <span class="tw-font-medium">{{ fw.metadata?.reference ? fw.metadata.reference + ' (' + fw.name + ')' : fw.name }}</span>
              </td>
            </ng-container>

            <!-- Version Column -->
            <ng-container matColumnDef="version">
              <th *matHeaderCellDef mat-header-cell>{{ labels.version }}</th>
              <td
                *matCellDef="let fw"
                class="!tw-font-base tw-font-mono"
                mat-cell>{{ fw.version }}</td>
            </ng-container>

            <!-- Product Column -->
            <ng-container matColumnDef="product">
              <th *matHeaderCellDef mat-header-cell>{{ labels.product }}</th>
              <td
                *matCellDef="let fw"
                class="!tw-font-base"
                mat-cell>
                <span class="tw-text-xs">{{ getProductLabel(fw.productId) }}</span>
              </td>
            </ng-container>

            <!-- Type Column -->
            <ng-container matColumnDef="type">
              <th *matHeaderCellDef mat-header-cell>{{ labels.type }}</th>
              <td
                *matCellDef="let fw"
                class="!tw-font-base"
                mat-cell>
                <div class="tw-flex tw-items-center tw-gap-2">
                  <mat-icon
                    *ngIf="uiFramework === 'material'"
                    class="s-5 text-grey-400">{{ getTypeIcon(fw.firmwareType) }}</mat-icon>
                  <ion-icon
                    *ngIf="uiFramework === 'ionic'"
                    class="text-grey-400"
                    [name]="getTypeIcon(fw.firmwareType)"></ion-icon>
                  <span class="tw-text-xs tw-capitalize">{{ getTypeLabel(fw.firmwareType) }}</span>
                </div>
              </td>
            </ng-container>

            <!-- Tags Column -->
            <ng-container matColumnDef="tags">
              <th *matHeaderCellDef mat-header-cell>{{ labels.tags }}</th>
              <td
                *matCellDef="let fw"
                class="!tw-font-base"
                mat-cell>
                <div
                  *ngIf="fw.metadata?.tags?.length"
                  class="tw-flex tw-flex-wrap tw-gap-1">
                  <mat-chip
                    *ngFor="let tag of fw.metadata?.tags"
                    class="chip-level-1">{{ tag }}</mat-chip>
                </div>
                <span
                  *ngIf="!fw.metadata?.tags?.length"
                  class="tw-text-xs text-grey-300 tw-italic">{{ labels.noMetadata }}</span>
              </td>
            </ng-container>

            <tr *matHeaderRowDef="['name', 'version', 'product', 'type', 'tags']" mat-header-row></tr>
            <tr
              *matRowDef="let row; columns: ['name', 'version', 'product', 'type', 'tags']; let odd = odd"
              class="hover"
              mat-row
              [class.bg-grey-100]="odd"
              (click)="selectFirmware(row)">
            </tr>
          </table>
        </div>

          <!-- Firmware List (Mobile) -->
          <div
            class="tw-flex-1 tw-flex-col tw-gap-2 tw-overflow-auto border-1 radius-4 tw-border-grey-100"
            id="firmware-list">
            <ng-container *ngIf="state.loadState?.status !== 'loading' && state.filteredFirmware?.length">
              <div
                *ngFor="let fw of state.filteredFirmware; let first = first; let odd = odd"
              class="tw-flex tw-flex-col tw-p-3 tw-gap-2 tw-border-grey-100 hover"
              [class.tw-border-t]="!first"
              [class.bg-grey-100]="odd"
              (click)="selectFirmware(fw)">
              <!-- Row 1: Type icon + Name -->
              <div class="tw-flex tw-items-center tw-gap-2">
                <mat-icon
                  *ngIf="uiFramework === 'material'"
                  class="s-5 text-grey-400">{{ getTypeIcon(fw.firmwareType) }}</mat-icon>
                <ion-icon
                  *ngIf="uiFramework === 'ionic'"
                  class="text-grey-400"
                  [name]="getTypeIcon(fw.firmwareType)"></ion-icon>
                <span class="tw-font-medium tw-truncate tw-flex-1">{{ fw.metadata?.reference ? fw.metadata?.reference + ' (' + fw.name + ')' : fw.name }}</span>
              </div>
              <!-- Row 2: Version + Product + Type label -->
              <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <span class="tw-text-sm tw-font-mono text-grey-400">{{ fw.version }}</span>
                <span class="tw-text-xs text-grey-300">{{ getProductLabel(fw.productId) }}</span>
                <span class="tw-text-xs tw-capitalize text-grey-300">{{ getTypeLabel(fw.firmwareType) }}</span>
              </div>
              <!-- Row 3: Tags (if any) -->
              <div
                *ngIf="fw.metadata?.tags?.length"
                class="tw-flex tw-flex-wrap tw-gap-1">
                <mat-chip
                  *ngFor="let tag of fw.metadata?.tags"
                  class="chip-level-1">{{ tag }}</mat-chip>
              </div>
            </div>
          </ng-container>
        </div>
      </ng-container>
    </div>

      <!-- Edit Panel (below selected firmware) -->
      <div
        *ngIf="state.selectedFirmware as selected"
        class="tw-flex tw-flex-col border-1 radius-4 tw-border-grey-100">
      <form
        class="tw-flex tw-flex-col tw-gap-4 tw-p-4"
        [formGroup]="editForm">
        <!-- Disclaimer -->
        <div class="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-rounded tw-bg-amber-50 tw-text-amber-700">
          <mat-icon
            *ngIf="uiFramework === 'material'"
            class="s-5 min-s-5 tw-mt-0.5">info</mat-icon>
          <ion-icon
            *ngIf="uiFramework === 'ionic'"
            class="s-5 min-s-5 tw-mt-0.5"
            name="information-circle-outline"></ion-icon>
          <span class="tw-text-sm">{{ labels.allFieldsRequired }}</span>
        </div>

        <!-- Reference -->
        <div class="tw-flex tw-flex-col tw-gap-2">
          <label
            class="tw-text-xs tw-font-medium tw-uppercase"
            [class.text-grey-400]="!hasFieldError('reference')"
            [class.tw-text-red-500]="hasFieldError('reference')">{{ labels.reference }}</label>
          <mat-form-field
            *ngIf="uiFramework === 'material'"
            class="custom-mat-form-field w-full custom-border-bottom"
            [class.tw-border-red-400]="hasFieldError('reference')">
            <input
              formControlName="reference"
              matInput
              [placeholder]="labels.referencePlaceholder"
              (input)="clearSaveError()">
          </mat-form-field>
          <ion-input
            *ngIf="uiFramework === 'ionic'"
            formControlName="reference"
            [placeholder]="labels.referencePlaceholder"
            (ionInput)="clearSaveError()">
          </ion-input>
          <span
            *ngIf="hasFieldError('reference')"
            class="tw-text-xs tw-text-red-500">{{ labels.missingReference }}</span>
        </div>

        <!-- Tags -->
        <div class="tw-flex tw-flex-col tw-gap-3">
          <label
            class="tw-text-xs tw-font-medium tw-uppercase"
            [class.text-grey-400]="!hasFieldError('tags')"
            [class.tw-text-red-500]="hasFieldError('tags')">{{ labels.tags }}</label>
          <span
            *ngIf="hasFieldError('tags')"
            class="tw-text-xs tw-text-red-500 tw--mt-2">{{ labels.missingTags }}</span>

          <!-- Factory Tags -->
          <div class="tw-flex tw-flex-col tw-gap-2">
            <span class="tw-text-xs text-grey-300 tw-uppercase">{{ labels.factoryTags }}</span>
            <div class="tw-flex tw-flex-wrap tw-gap-2">
              <mat-chip
                *ngFor="let tag of factoryTags"
                [ngClass]="isTagSelected(tag) ? 'chip-level-0' : 'chip-inactive'"
                (click)="toggleTag(tag)">
                {{ tag }}
                <mat-icon
                  *ngIf="isTagSelected(tag)"
                  class="s-4"
                  matChipTrailingIcon>check</mat-icon>
              </mat-chip>
            </div>
          </div>

          <!-- Operational Tags -->
          <div class="tw-flex tw-flex-col tw-gap-2">
            <span class="tw-text-xs text-grey-300 tw-uppercase">{{ labels.operationalTags }}</span>
            <div class="tw-flex tw-flex-wrap tw-gap-2">
              <mat-chip
                *ngFor="let tag of operationalTags"
                [ngClass]="isTagSelected(tag) ? 'chip-level-0' : 'chip-inactive'"
                (click)="toggleTag(tag)">
                {{ tag }}
                <mat-icon
                  *ngIf="isTagSelected(tag)"
                  class="s-4"
                  matChipTrailingIcon>check</mat-icon>
              </mat-chip>
            </div>
          </div>

          <!-- Custom Tag Input -->
          <div class="tw-flex tw-flex-col tw-gap-2">
            <span class="tw-text-xs text-grey-300 tw-uppercase">{{ labels.customTags }}</span>
            <div class="tw-flex tw-gap-2 tw-items-center">
              <mat-form-field
                *ngIf="uiFramework === 'material'"
                class="custom-mat-form-field tw-flex-1 custom-border-bottom">
                <input
                  matInput
                  [ngModelOptions]="{standalone: true}"
                  [placeholder]="labels.addTag"
                  [(ngModel)]="customTagInput"
                  (keyup.enter)="addCustomTag(customTagInput)">
              </mat-form-field>
              <button
                *ngIf="uiFramework === 'material'"
                class="custom-mat-button-secondary custom-mat-button-border"
                mat-icon-button
                (click)="addCustomTag(customTagInput)">
                <mat-icon>add</mat-icon>
              </button>
              <ion-input
                *ngIf="uiFramework === 'ionic'"
                class="tw-flex-1"
                [ngModelOptions]="{standalone: true}"
                [placeholder]="labels.addTag"
                [(ngModel)]="customTagInput"
                (keyup.enter)="addCustomTag(customTagInput)">
              </ion-input>
              <ion-button
                *ngIf="uiFramework === 'ionic'"
                fill="clear"
                (click)="addCustomTag(customTagInput)">
                <ion-icon name="add"></ion-icon>
              </ion-button>
            </div>
          </div>

          <!-- Selected Tags -->
          <div
            *ngIf="editForm.value.tags?.length"
            class="tw-flex tw-flex-col tw-gap-2">
            <span class="tw-text-xs text-grey-300 tw-uppercase">{{ labels.selectedTags }}</span>
            <mat-chip-set class="tw-flex tw-flex-wrap tw-gap-2">
              <mat-chip
                *ngFor="let tag of editForm.value.tags"
                class="chip-level-0"
                (removed)="removeTag(tag)">
                {{ tag }}
                <button matChipRemove>
                  <mat-icon class="s-4">close</mat-icon>
                </button>
              </mat-chip>
            </mat-chip-set>
          </div>
        </div>

        <!-- Logger Type (only for logger firmware) -->
        <div
          *ngIf="selected.firmwareType === 'logger'"
          class="tw-flex tw-flex-col tw-gap-2">
          <label
            class="tw-text-xs tw-font-medium tw-uppercase"
            [class.text-grey-400]="!hasFieldError('loggerType')"
            [class.tw-text-red-500]="hasFieldError('loggerType')">{{ labels.loggerType }}</label>
          <mat-form-field
            *ngIf="uiFramework === 'material'"
            class="custom-mat-form-field w-full custom-border-bottom"
            [class.tw-border-red-400]="hasFieldError('loggerType')">
            <mat-select
              formControlName="loggerType"
              [placeholder]="labels.loggerTypePlaceholder"
              (selectionChange)="clearSaveError()">
              <mat-option *ngFor="let lt of loggerTypes" [value]="lt.label">{{ lt.label }}</mat-option>
            </mat-select>
          </mat-form-field>
          <ion-select
            *ngIf="uiFramework === 'ionic'"
            formControlName="loggerType"
            interface="popover"
            [placeholder]="labels.loggerTypePlaceholder"
            (ionChange)="clearSaveError()">
            <ion-select-option *ngFor="let lt of loggerTypes" [value]="lt.label">{{ lt.label }}</ion-select-option>
          </ion-select>
          <span
            *ngIf="hasFieldError('loggerType')"
            class="tw-text-xs tw-text-red-500">{{ labels.missingLoggerType }}</span>
        </div>

        <!-- Serial Patterns (only for inverter firmware) -->
        <div
          *ngIf="selected.firmwareType === 'inverter'"
          class="tw-flex tw-flex-col tw-gap-2">
          <label
            class="tw-text-xs tw-font-medium tw-uppercase"
            [class.text-grey-400]="!hasFieldError('serialPatterns')"
            [class.tw-text-red-500]="hasFieldError('serialPatterns')">{{ labels.serialPatterns }}</label>
          <mat-form-field
            *ngIf="uiFramework === 'material'"
            class="custom-mat-form-field w-full custom-border-bottom"
            [class.tw-border-red-400]="hasFieldError('serialPatterns')">
            <mat-select
              formControlName="serialPatterns"
              multiple
              [placeholder]="labels.serialPatternsPlaceholder"
              (selectionChange)="clearSaveError()">
              <mat-option *ngFor="let sp of inverterSerialPatterns" [value]="sp.label">{{ sp.label }} — {{ sp.modelFamily }}</mat-option>
            </mat-select>
          </mat-form-field>
          <ion-select
            *ngIf="uiFramework === 'ionic'"
            formControlName="serialPatterns"
            interface="popover"
            [multiple]="true"
            [placeholder]="labels.serialPatternsPlaceholder"
            (ionChange)="clearSaveError()">
            <ion-select-option *ngFor="let sp of inverterSerialPatterns" [value]="sp.label">{{ sp.label }} — {{ sp.modelFamily }}</ion-select-option>
          </ion-select>
          <span
            *ngIf="hasFieldError('serialPatterns')"
            class="tw-text-xs tw-text-red-500">{{ labels.missingSerialPatterns }}</span>
        </div>

        <!-- Release Date -->
        <div class="tw-flex tw-flex-col tw-gap-2">
          <label class="tw-text-xs tw-font-medium text-grey-400 tw-uppercase">{{ labels.releaseDate }}</label>
          <mat-form-field
            *ngIf="uiFramework === 'material'"
            class="custom-mat-form-field w-full custom-border-bottom">
            <input
              formControlName="releaseDate"
              matInput
              [placeholder]="labels.releaseDatePlaceholder"
              [matDatepicker]="releaseDatePicker"
              (dateChange)="clearSaveError()">
            <mat-datepicker-toggle
              matSuffix
              [for]="releaseDatePicker">
            </mat-datepicker-toggle>
            <mat-datepicker #releaseDatePicker></mat-datepicker>
          </mat-form-field>
          <ion-input
            *ngIf="uiFramework === 'ionic'"
            formControlName="releaseDate"
            type="date"
            (ionInput)="clearSaveError()">
          </ion-input>
        </div>

        <!-- Notes -->
        <div class="tw-flex tw-flex-col tw-gap-2">
          <label class="tw-text-xs tw-font-medium text-grey-400 tw-uppercase">{{ labels.notes }}</label>
          <mat-form-field
            *ngIf="uiFramework === 'material'"
            class="custom-mat-form-field w-full custom-border-bottom">
            <input
              formControlName="notes"
              matInput
              [placeholder]="labels.notesPlaceholder">
          </mat-form-field>
          <ion-input
            *ngIf="uiFramework === 'ionic'"
            formControlName="notes"
            [placeholder]="labels.notesPlaceholder">
          </ion-input>
        </div>

        <!-- Save Error Message -->
        <div
          *ngIf="state.saveState?.status === 'error'"
          class="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-rounded tw-bg-red-50 tw-text-red-600">
          <mat-icon
            *ngIf="uiFramework === 'material'"
            class="s-5">error</mat-icon>
          <ion-icon
            *ngIf="uiFramework === 'ionic'"
            name="alert-circle"></ion-icon>
          <span class="tw-text-sm">{{ state.saveState?.error }}</span>
        </div>

        <!-- Save Success Message -->
        <div
          *ngIf="state.saveState?.status === 'success'"
          class="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-rounded tw-bg-green-50 tw-text-green-600">
          <mat-icon
            *ngIf="uiFramework === 'material'"
            class="s-5">check_circle</mat-icon>
          <ion-icon
            *ngIf="uiFramework === 'ionic'"
            name="checkmark-circle"></ion-icon>
          <span class="tw-text-sm">{{ labels.savedSuccess }}</span>
        </div>

        <!-- Actions -->
        <div class="tw-flex tw-justify-between tw-gap-2 tw-pt-4 border-t-1 tw-border-grey-100">
          <!-- Delete (left side) -->
          <div>
            <button
              *ngIf="uiFramework === 'material' && selected.metadata"
              class="custom-mat-button-border"
              color="warn"
              mat-button
              type="button"
              [disabled]="state.saveState?.status === 'saving'"
              (click)="deleteAllMetadata()">
              <mat-icon class="s-5 m-0">delete</mat-icon>
              {{ labels.deleteMetadata }}
            </button>
            <ion-button
              *ngIf="uiFramework === 'ionic' && selected.metadata"
              color="danger"
              fill="clear"
              [disabled]="state.saveState?.status === 'saving'"
              (click)="deleteAllMetadata()">
              <ion-icon
                name="trash-outline"
                slot="start"></ion-icon>
              {{ labels.deleteMetadata }}
            </ion-button>
          </div>

          <!-- Save/Cancel (right side) -->
          <div class="tw-flex tw-gap-2">
            <button
              *ngIf="uiFramework === 'material'"
              class="custom-mat-button-secondary custom-mat-button-border"
              mat-button
              type="button"
              (click)="cancelEdit()">
              {{ labels.cancel }}
            </button>
            <button
              *ngIf="uiFramework === 'material'"
              class="custom-mat-button-primary custom-mat-button-border"
              mat-button
              type="button"
              [disabled]="state.saveState?.status === 'saving'"
              (click)="saveMetadata()">
              <mat-spinner
                *ngIf="state.saveState?.status === 'saving'"
                diameter="20"></mat-spinner>
              <span *ngIf="state.saveState?.status !== 'saving'">{{ labels.save }}</span>
            </button>

            <ion-button
              *ngIf="uiFramework === 'ionic'"
              fill="clear"
              (click)="cancelEdit()">
              {{ labels.cancel }}
            </ion-button>
            <ion-button
              *ngIf="uiFramework === 'ionic'"
              [disabled]="state.saveState?.status === 'saving'"
              (click)="saveMetadata()">
              <ion-spinner
                *ngIf="state.saveState?.status === 'saving'"
                name="crescent"></ion-spinner>
              <span *ngIf="state.saveState?.status !== 'saving'">{{ labels.save }}</span>
            </ion-button>
          </div>
        </div>
      </form>
    </div>
  </div>
  </div>
</ng-container>
